import * as React from 'react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';
import { Input } from './input';

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  // onClear lets the caller hook into the clear (×) button explicitly.
  // When omitted the component synthesizes a change event with empty
  // value and forwards it to onChange, so callers wiring only onChange
  // still get the field cleared.
  onClear?: () => void;
  // Localised aria-label for the clear button. Defaults to plain
  // English so callers that don't care about i18n still work.
  clearLabel?: string;
  // Extra classes for the relative wrapper div — use this when the
  // caller needs to size the field (`w-full sm:w-72`, `flex-1`, etc.).
  // `className` on the component itself still flows to the <input>.
  wrapperClassName?: string;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, wrapperClassName, value, onChange, onClear, clearLabel = 'Clear search', ...rest }, ref) => {
    const hasValue = typeof value === 'string' && value.length > 0;
    return (
      <div className={cn('relative', wrapperClassName)}>
        <MagnifyingGlass
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          weight="bold"
          aria-hidden
        />
        <Input
          ref={ref}
          type="search"
          value={value}
          onChange={onChange}
          className={cn('pl-9', hasValue && 'pr-9', className)}
          {...rest}
        />
        {hasValue && (
          <button
            type="button"
            onClick={() => {
              if (onClear) {
                onClear();
                return;
              }
              if (onChange) {
                const synthetic = {
                  target: { value: '' },
                  currentTarget: { value: '' },
                } as React.ChangeEvent<HTMLInputElement>;
                onChange(synthetic);
              }
            }}
            aria-label={clearLabel}
            className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X weight="bold" className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  },
);
SearchInput.displayName = 'SearchInput';
